// Two addresses for one page, and the one that should be a redirect.
//
// M-13. A handful of routes have a French name and an English name - the
// calculators are /en/calculators and /fr/calculatrices, an agency showcase is
// /en/agency/<slug> and /fr/agence/<slug>. Both directories exist under
// app/[lang], and [lang] matches either language, so all four combinations
// resolved with a 200:
//
//   /en/calculators    /en/calculatrices
//   /fr/calculatrices  /fr/calculators
//
// The canonical tags were right, which is what kept this from being worse, but
// a canonical is a hint a crawler may decline to follow, and it does nothing at
// all for the visitor: two addresses for the same page means links, bookmarks
// and shares split between them, and an agency handing out the wrong one has no
// way to know.
//
import { langPrefix } from '@/lib/locale-path';
import { CALCULATORS_DATA } from '@/lib/calculators-data';

// A 301 settles it. The crossed address keeps working - nothing that was ever
// linked breaks - and everything that follows it arrives at the one address the
// page claims as its own.

/** Segment after the language, per language. */
interface LocalizedSegment {
  en: string;
  fr: string;
}

export const LOCALIZED_SEGMENTS: LocalizedSegment[] = [
  { en: 'calculators', fr: 'calculatrices' },
  { en: 'agency', fr: 'agence' },
  // Every listing in Canada, searched and filtered (listing browse plan, step 5).
  { en: 'listings', fr: 'annonces' },
  // Every rental in Canada (rentals, step 2, 2026-09-24): "locations" is the
  // French for rentals, as on Centris and DuProprio.
  { en: 'rentals', fr: 'locations' },
  // The comparison (compare backlog, item 3, 2026-09-28).
  { en: 'compare', fr: 'comparer' },
];

/** The comparison page in this language, with the listings to compare. */
export function comparePath(lang: string, ids: string[] = []): string {
  const path = lang === 'fr' ? '/fr/comparer' : '/compare';
  return ids.length ? `${path}?ids=${ids.join(',')}` : path;
}

/** Whether a pathname is the comparison page, in either language. */
export const isComparePath = (pathname: string | null | undefined) => /^(?:\/(?:en|fr))?\/(compare|comparer)(\/|$)/.test(pathname || '');

/** Every segment that is the name of a localized route, in either language. */
const ALL_SEGMENTS = new Map<string, LocalizedSegment>();
for (const pair of LOCALIZED_SEGMENTS) {
  ALL_SEGMENTS.set(pair.en, pair);
  ALL_SEGMENTS.set(pair.fr, pair);
}

/**
 * The address this path should redirect to, or null when it is already right.
 *
 * `pathname` starts with a slash and includes the language segment. Anything
 * after the localized segment is carried over untouched, so
 * /fr/agency/royal-realty becomes /fr/agence/royal-realty.
 */
export function canonicalLocalizedPath(pathname: string): string | null {
  const parts = pathname.split('/');
  // ['', lang, segment, ...rest]
  const lang = parts[1];
  const segment = parts[2];
  if (lang !== 'en' && lang !== 'fr') return null;
  if (!segment) return null;

  const pair = ALL_SEGMENTS.get(segment);
  if (!pair) return null;

  const correct = lang === 'fr' ? pair.fr : pair.en;
  if (segment === correct) return null;

  parts[2] = correct;
  return parts.join('/');
}

/**
 * A calculator's own address in this language. The two languages name each calculator differently
 * (/en/calculators/mortgage-affordability, /fr/calculatrices/capacite-emprunt), so switching language on a
 * calculator page must swap the last word too: the language switcher used to build /en/calculators/capacite-emprunt,
 * which only worked because proxy.ts redirects it, and cost the visitor and a crawler an extra request.
 * Returns the path unchanged when it is not a calculator page or the calculator is unknown.
 */
export function localizeCalculatorPath(pathname: string): string {
  // As the visitor sees it: /calculators/x (English) or /fr/calculatrices/x; the old /en/calculators/x is read too.
  const m = /^(?:\/(en|fr))?\/(calculators|calculatrices)\/([^/?#]+)(.*)$/.exec(pathname);
  if (!m) return pathname;
  const [, prefix, , slug, rest] = m;
  const lang = prefix === 'fr' ? 'fr' : 'en';
  const calc = CALCULATORS_DATA.find((c) => c.slug_en === slug || c.slug_fr === slug);
  if (!calc) return pathname;
  return `${langPrefix(lang)}/${lang === 'fr' ? 'calculatrices' : 'calculators'}/${lang === 'fr' ? calc.slug_fr : calc.slug_en}${rest}`;
}
